/**
 * Feedback Modal Component
 * Main modal for collecting user feedback
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Modal,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { FeedbackCategory } from '@/types/feedback.types';
import { submitFeedback } from '@/services/feedback/feedbackService';
import { CategoryTagPicker } from './CategoryTagPicker';
import { StarRating } from './StarRating';

interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
  screenName?: string;
}

const MAX_LENGTH = 1000;
const SHOW_COUNTER_AT = 900;

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ visible, onClose, screenName }) => {
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [customCategoryText, setCustomCategoryText] = useState('');
  const [urgency, setUrgency] = useState(0);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (isSubmitting) return;
    setCategories([]);
    setCustomCategoryText('');
    setUrgency(0);
    setDescription('');
    onClose();
  };

  const handleSubmit = async () => {
    // Validate
    if (categories.length === 0) {
      Alert.alert('Required', 'Please select at least one category');
      return;
    }

    // Validate "other" category requires custom text
    if (categories.includes('other') && !customCategoryText.trim()) {
      Alert.alert('Required', 'Please specify what "Other" category means');
      return;
    }

    if (!description.trim()) {
      Alert.alert('Required', 'Please enter a description');
      return;
    }

    setIsSubmitting(true);

    const result = await submitFeedback(
      {
        categories,
        customCategoryText: customCategoryText.trim() || undefined,
        urgency: urgency > 0 ? urgency : undefined,
        description,
      },
      screenName
    );

    setIsSubmitting(false);

    if (result.success) {
      Alert.alert(
        '✅ Thank You!',
        "Your feedback helps us improve. We'll review it within 24 hours.",
        [{ text: 'OK', onPress: handleClose }]
      );
    } else {
      Alert.alert('Error', result.error || 'Failed to submit feedback. Please try again.');
    }
  };

  const showCounter = description.length >= SHOW_COUNTER_AT;
  const remaining = MAX_LENGTH - description.length;
  const canSubmit =
    categories.length > 0 &&
    description.trim().length > 0 &&
    !isSubmitting &&
    // Validate "other" requires custom text
    (!categories.includes('other') || customCategoryText.trim().length > 0);

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>📝 Send Feedback</Text>
              <TouchableOpacity onPress={handleClose} disabled={isSubmitting}>
                <Ionicons name="close" size={28} color={Colors.text.primary} />
              </TouchableOpacity>
            </View>

            {/* Content */}
            <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
              {/* Category Tag Picker */}
              <CategoryTagPicker
                selectedCategories={categories}
                onCategoriesChange={setCategories}
                customCategoryText={customCategoryText}
                onCustomTextChange={setCustomCategoryText}
                disabled={isSubmitting}
              />

              {/* Urgency Rating */}
              <StarRating rating={urgency} onRatingChange={setUrgency} disabled={isSubmitting} />

              {/* Description */}
              <View style={styles.inputContainer}>
                <Text style={styles.label}>
                  Tell us more <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={styles.textArea}
                  multiline
                  placeholder={
                    "Describe the issue or suggestion.\n\nExample: When I tap on AAPL stock, the chart doesn't load."
                  }
                  placeholderTextColor={Colors.text.tertiary}
                  value={description}
                  onChangeText={setDescription}
                  maxLength={MAX_LENGTH}
                  editable={!isSubmitting}
                  textAlignVertical="top"
                />
                {showCounter && (
                  <Text style={[styles.counter, remaining < 100 && styles.counterWarning]}>
                    {remaining} characters remaining
                  </Text>
                )}
              </View>

              {/* Info Text */}
              <View style={styles.infoBox}>
                <Ionicons name="information-circle" size={16} color={Colors.text.secondary} />
                <Text style={styles.infoText}>
                  Your feedback is collected to help us improve the app.
                </Text>
              </View>
            </ScrollView>

            {/* Footer */}
            <View style={styles.footer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={handleClose}
                disabled={isSubmitting}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.submitButton, !canSubmit && styles.disabledButton]}
                onPress={handleSubmit}
                disabled={!canSubmit}
              >
                {isSubmitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Submit Feedback</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modal: {
    backgroundColor: Colors.background.primary,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.text.primary,
  },
  content: {
    padding: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 8,
  },
  required: {
    color: Colors.status.error,
  },
  textArea: {
    backgroundColor: Colors.background.tertiary,
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    color: Colors.text.primary,
    minHeight: 120,
    maxHeight: 200,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  counter: {
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'right',
    marginTop: 4,
  },
  counterWarning: {
    color: Colors.status.warning,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: Colors.background.tertiary,
    padding: 12,
    borderRadius: 8,
    gap: 8,
    marginTop: 8,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    color: Colors.text.secondary,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: Colors.background.tertiary,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  submitButton: {
    backgroundColor: Colors.accent.primary,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  disabledButton: {
    opacity: 0.5,
  },
});
